import { useEffect, useState } from 'react';
import type { PieceModel } from '@doer/frontend-components/creativity-editor';
import { useBanners } from '../../../context/banners';
import { isFormCreativity } from '../../../types/banner';
import { addCacheBusting } from '../../../utils/cache-busting';
import type { RoutedFormData } from '../../../types/templates';

type ReopenPiece = {
  loading: boolean;
  initialFormData?: RoutedFormData;
  savedModels?: Map<string, PieceModel>;
};

// Keeps the loader visible long enough to read as a spinner, not a flash, when the fetch resolves instantly.
const MIN_SPINNER_MS = 500;

export const useReopenPiece = (contentId?: string): ReopenPiece => {
  const { findBannerById } = useBanners();
  const [state, setState] = useState<ReopenPiece>({ loading: Boolean(contentId) });

  useEffect(() => {
    if (!contentId) {
      setState({ loading: false });
      return;
    }

    const item = findBannerById(contentId);
    // title lives at the banner root, not in config — reinject it so edits don't wipe it.
    const initialFormData =
      item && isFormCreativity(item)
        ? ({ ...(item.config as object), title: item.title } as RoutedFormData)
        : undefined;
    const models = item?.editorModels ?? [];
    const startedAt = Date.now();
    let cancelled = false;

    (async () => {
      let next: ReopenPiece;
      try {
        const entries = await Promise.all(
          models.map(async ({ resolutionId, url }): Promise<[string, PieceModel]> => {
            const response = await fetch(addCacheBusting(url, item?.updatedAt ?? item?.date ?? new Date()));
            return [resolutionId, (await response.json()) as PieceModel];
          })
        );
        next = { loading: false, initialFormData, savedModels: new Map(entries) };
      } catch (e) {
        console.error('[useReopenPiece] fetch failed', e);
        next = { loading: false, initialFormData };
      }

      const wait = Math.max(0, MIN_SPINNER_MS - (Date.now() - startedAt));
      setTimeout(() => {
        if (!cancelled) setState(next);
      }, wait);
    })();

    return () => {
      cancelled = true;
    };
  }, [contentId, findBannerById]);

  return state;
};
